import React, { useState } from 'react';

interface Task {
  id: string;
  number: number;
  title: string;
  description: string;
  requirements: Array<{
    text: string;
    subitems?: string[];
  }>;
  verification: string[];
}

const tasks: Task[] = [
  {
    id: 'task-1',
    number: 1,
    title: 'Create User Accounts',
    description: 'Set up new user accounts for team members with appropriate configurations and home directories.',
    requirements: [
      {
        text: 'Create user accounts for the following users:',
        subitems: ['alice', 'bob', 'charlie', 'dave', 'emma']
      },
      {
        text: 'Ensure each user has a home directory'
      },
      {
        text: 'Set a secure password for each user'
      },
      {
        text: 'Configure bash as the default shell for all users'
      }
    ],
    verification: [
      'All five user accounts exist in the system',
      'Each user has a home directory in /home',
      'All users can successfully log in',
      'Each user has /bin/bash as their default shell'
    ]
  },
  {
    id: 'task-2',
    number: 2,
    title: 'Configure User Groups',
    description: 'Establish groups for organizational structure and assign users to appropriate groups.',
    requirements: [
      {
        text: 'Create the following groups:',
        subitems: ['developers', 'webadmin', 'readonly', 'backup-ops']
      },
      {
        text: 'Assign users to groups:',
        subitems: [
          'alice → developers group',
          'bob → developers and webadmin groups',
          'charlie → readonly group', 
          'dave → backup-ops group',
          'emma → readonly group'
        ]
      },
      {
        text: 'Verify group memberships are correctly configured'
      }
    ],
    verification: [
      'All four groups exist in the system',
      'Alice is member of developers group',
      'Bob is member of developers and webadmin groups',
      'Charlie and Emma are members of readonly group',
      'Dave is member of backup-ops group'
    ]
  },
  {
    id: 'task-3',
    number: 3,
    title: 'Create Project Directory Structure',
    description: 'Set up a shared project directory structure with appropriate group ownership and permissions.',
    requirements: [
      { text: 'Create /projects as the main root' },
      {
        text: 'Create the following subdirectories with group ownership:',
        subitems: [
          '/projects/webdev (group: developers)',
          '/projects/database (group: webadmin)',
          '/projects/backup (group: backup-ops)',
          '/projects/public (group: users)'
        ]
      },
      { text: 'Enable SGID on all project subdirectories to enforce group inheritance' }
    ],
    verification: [
      'The /projects directory exists',
      'All four subdirectories are created with correct group ownership',
      'SGID bit is set on each subdirectory to enforce group inheritance'
    ]
  },
  {
    id: 'task-4',
    number: 4,
    title: 'Set File Permissions',
    description: 'Apply specific file permissions to ensure proper security and access control.',
    requirements: [
      {
        text: 'Create test files in each project directory'
      },
      {
        text: 'Set the following permission requirements:',
        subitems: [
          'Development files: read/write for developers',
          'Web files: read for all, write for webadmin',
          'Backup files: read/write for backup-ops only',
          'Shared files: read for all, write for group owners'
        ]
      },
      {
        text: 'Apply appropriate permission modes using octal notation'
      },
      {
        text: 'Verify permissions are correctly applied'
      }
    ],
    verification: [
      'Test files exist in all directories',
      'Development files have correct permissions',
      'Web files are readable by all, writable by webadmin',
      'Backup files are accessible only by backup-ops',
      'Shared files have appropriate group permissions'
    ]
  },
  {
    id: 'task-4b',
    number: 4,
    title: 'Web Server Security File',
    description: 'Create a secure configuration file for the web server with correct ownership and permissions.',
    requirements: [
      {
        text: 'Create /var/www/html/config/db.conf with database credentials (placeholder values are fine)'
      },
      { text: 'Set ownership to www-data:webadmin' },
      { text: 'Set permissions to 640' }
    ],
    verification: [
      '/var/www/html/config/db.conf exists',
      'Ownership is www-data:webadmin',
      'Permissions are 640'
    ]
  },
  {
    id: 'task-4c',
    number: 4,
    title: 'SSH Keys & Permissions',
    description: 'Generate SSH keys for specified users and set strict permissions.',
    requirements: [
      { text: 'Generate RSA 4096-bit keys for alice and bob; Ed25519 keys for charlie' },
      { text: "Set ~/.ssh directory permissions to 700 and private keys to 600; public keys to 644" }
    ],
    verification: [
      'Keys exist for alice, bob, and charlie',
      'Permissions are correctly set on ~/.ssh and key files'
    ]
  },
  {
    id: 'task-5',
    number: 5,
    title: 'Configure Special Permissions',
    description: 'Set up special permissions including SUID, SGID, and sticky bit where appropriate.',
    requirements: [
      {
        text: 'Apply SGID to the shared directory for group inheritance'
      },
      {
        text: 'Set sticky bit on the shared directory to protect files'
      },
      {
        text: 'Create a script that needs elevated privileges'
      },
      {
        text: 'Configure appropriate special permissions on the script'
      },
      {
        text: 'Test that special permissions work as expected'
      }
    ],
    verification: [
      'SGID is set on /project/shared directory',
      'Sticky bit is set on /project/shared directory',
      'New files inherit group ownership in shared directory',
      'Users cannot delete other users files in shared directory',
      'Special permissions are functioning correctly'
    ]
  },
  {
    id: 'task-6',
    number: 6,
    title: 'Implement Access Control Lists',
    description: 'Use ACLs to provide fine-grained access control beyond traditional permissions.',
    requirements: [
      {
        text: 'Enable ACL support on the filesystem if needed'
      },
      {
        text: 'Grant specific permissions using ACLs:',
        subitems: [
          'Give charlie read access to /projects/webdev (user:charlie:r-x)',
          'Give dave read access to /projects/database (user:dave:r-x)',
          'Set default ACLs on shared directories as needed'
        ]
      },
      {
        text: 'Configure ACL masks for maximum permissions'
      },
      {
        text: 'Verify ACL permissions are working correctly'
      }
    ],
    verification: [
      'ACLs are enabled and functioning',
      'Alice has read access to backup directory via ACL',
      'Emma can write to specified development file',
      'Default ACLs are set on shared directory',
      'ACL permissions override traditional permissions correctly'
    ]
  },
  {
    id: 'task-7',
    number: 7,
    title: 'Sudo and Umask Configuration',
    description: 'Grant sudo access and set default umask via profile script.',
    requirements: [
      { text: 'Add bob to the sudo group for administrative access' },
      { text: 'Create /etc/profile.d/custom-umask.sh to define default umask values' }
    ],
    verification: [
      'bob is a member of the sudo group',
      '/etc/profile.d/custom-umask.sh exists and is executable'
    ]
  }
];

const LinuxFilePermissionsInstructions: React.FC = () => {
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [completedChecks, setCompletedChecks] = useState<Set<string>>(new Set());
  const [completedTasks, setCompletedTasks] = useState<Set<number>>(new Set());

  const handleTaskClick = (index: number) => {
    setCurrentTaskIndex(index);
  };

  const handleNextTask = () => {
    if (currentTaskIndex < tasks.length - 1) {
      setCurrentTaskIndex(currentTaskIndex + 1);
    } else {
      setCurrentTaskIndex(0);
    }
  };

  const handlePreviousTask = () => {
    if (currentTaskIndex > 0) {
      setCurrentTaskIndex(currentTaskIndex - 1);
    }
  };

  const toggleVerification = (taskId: string, checkIndex: number) => {
    const checkId = `${taskId}-check-${checkIndex}`;
    const newChecks = new Set(completedChecks);
    
    if (newChecks.has(checkId)) {
      newChecks.delete(checkId);
    } else {
      newChecks.add(checkId);
    }
    
    setCompletedChecks(newChecks);

    // Check if all verifications for this task are completed
    const currentTask = tasks[currentTaskIndex];
    const allChecksCompleted = currentTask.verification.every(
      (_, idx) => newChecks.has(`${currentTask.id}-check-${idx}`)
    );

    if (allChecksCompleted && !completedTasks.has(currentTaskIndex)) {
      const newCompletedTasks = new Set(completedTasks);
      newCompletedTasks.add(currentTaskIndex);
      setCompletedTasks(newCompletedTasks);
    }
  };

  const currentTask = tasks[currentTaskIndex];
  const progressPercentage = ((currentTaskIndex + 1) / tasks.length) * 100;

  return (
    <div className="bg-[#232937] rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#10b981] to-[#059669] px-8 py-6">
        <h2 className="text-2xl font-semibold text-[#1a1f2e]">Instructions</h2>
      </div>

      {/* Content Area - Increased to 1800px for less scrolling */}
      <div className="flex h-[1800px]">
        {/* Sidebar Navigation */}
        <div className="w-[280px] bg-[#1a1f2e] border-r border-[#2d3748] overflow-y-auto">
          {tasks.map((task, index) => (
            <div
              key={task.id}
              onClick={() => handleTaskClick(index)}
              className={`flex items-center px-5 py-4 cursor-pointer border-b border-[#2d3748] hover:bg-[#2d3748] transition-all relative ${
                index === currentTaskIndex ? 'bg-[#2d3748] border-l-[3px] border-l-[#10b981] pl-[17px]' : ''
              }`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold mr-3 flex-shrink-0 ${
                completedTasks.has(index) ? 'bg-[#059669]' : 'bg-[#10b981]'
              } text-[#1a1f2e]`}>
                {task.number}
              </div>
              <div className={`text-sm ${index === currentTaskIndex ? 'text-[#e2e8f0] font-medium' : 'text-[#cbd5e1]'}`}>
                {task.title}
              </div>
              {completedTasks.has(index) && (
                <span className="absolute right-5 text-[#10b981] font-bold text-sm">✓</span>
              )}
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8 bg-[#1e293b] overflow-y-auto">
          <h3 className="text-[22px] font-semibold text-[#e2e8f0] mb-4">
            Task {currentTask.number}: {currentTask.title}
          </h3>
          <p className="text-[#94a3b8] mb-8 leading-[1.6] text-[15px]">
            {currentTask.description}
          </p>

          {/* Requirements Section */}
          <div className="mb-8">
            <h4 className="text-[#10b981] text-xs uppercase tracking-[1px] mb-4 font-semibold flex items-center">
              <span className="w-5 h-[2px] bg-[#10b981] mr-[10px]"></span>
              What You Need To Do
            </h4>
            
            {currentTask.requirements.map((requirement, reqIndex) => (
              <div key={reqIndex} className="bg-[#232937] border border-[#2d3748] rounded-lg p-4 mb-3 hover:border-[#374151] hover:shadow-md transition-all">
                <div className="text-[#e2e8f0] text-sm leading-[1.8]">
                  <span className="font-medium">{reqIndex + 1}. {requirement.text}</span>
                  {requirement.subitems && (
                    <ul className="mt-2 ml-5 space-y-1">
                      {requirement.subitems.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-[#10b981] mr-2 mt-0.5">•</span>
                          <span className="text-[#cbd5e1]">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Verification Section */}
          <div className="bg-[#232937] border-2 border-dashed border-[#475569] rounded-lg p-5 mt-6">
            <div className="flex items-center text-[#10b981] font-semibold mb-4 text-base">
              <span className="w-5 h-5 bg-[#10b981] text-[#1a1f2e] rounded-full flex items-center justify-center text-xs mr-[10px]">
                ✓
              </span>
              Verification
            </div>
            
            {currentTask.verification.map((check, checkIndex) => (
              <div
                key={checkIndex}
                onClick={() => toggleVerification(currentTask.id, checkIndex)}
                className="flex items-center p-[10px_12px] bg-[#1a1f2e] rounded-md mb-2 cursor-pointer hover:bg-[#1e293b] hover:translate-x-[3px] transition-all last:mb-0"
              >
                <div className={`w-[18px] h-[18px] border-2 border-[#10b981] rounded flex items-center justify-center mr-3 flex-shrink-0 transition-all ${
                  completedChecks.has(`${currentTask.id}-check-${checkIndex}`) ? 'bg-[#10b981]' : ''
                }`}>
                  {completedChecks.has(`${currentTask.id}-check-${checkIndex}`) && (
                    <span className="text-[#1a1f2e] text-xs font-bold">✓</span>
                  )}
                </div>
                <span className="text-[#cbd5e1] text-sm">{check}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Progress Footer */}
      <div className="flex items-center justify-between px-6 py-4 bg-[#1a1f2e] border-t border-[#2d3748]">
        <div className="text-[13px] text-[#94a3b8]">
          Progress: Task {currentTaskIndex + 1} of {tasks.length}
        </div>
        
        <div className="w-[200px] h-[6px] bg-[#2d3748] rounded-[3px] overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-[#10b981] to-[#34d399] transition-all duration-500 rounded-[3px]"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>

        <div className="flex gap-[10px]">
          <button
            onClick={handlePreviousTask}
            disabled={currentTaskIndex === 0}
            className="px-4 py-2 bg-[#2d3748] border border-[#374151] text-[#cbd5e1] rounded-md text-[13px] cursor-pointer transition-all hover:bg-[#374151] hover:text-[#e2e8f0] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={handleNextTask}
            className="px-4 py-2 bg-[#10b981] border border-[#10b981] text-[#1a1f2e] font-semibold rounded-md text-[13px] cursor-pointer transition-all hover:bg-[#059669] hover:border-[#059669]"
          >
            {currentTaskIndex === tasks.length - 1 ? 'Return to Task 1' : 'Next Task'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LinuxFilePermissionsInstructions;
